import { useState, type ReactNode } from "react";
import { ImageOff, Eye, Loader2 } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { ImageViewerModal } from "./ImageViewerModal";

export type ImageAspectRatio = "square" | "video" | "wide" | "auto";
export type ImageFit = "cover" | "contain" | "fill" | "none";

export interface ImageProps {
  src?: string | null;
  alt?: string;
  /** Aspect ratio constraint */
  aspectRatio?: ImageAspectRatio;
  /** Object fit style */
  fit?: ImageFit;
  /** Allow clicking image to open full-screen lightbox preview */
  previewable?: boolean;
  /** Custom fallback element when image fails to load */
  fallback?: ReactNode;
  /** ClassName applied to container wrapper */
  className?: string;
  /** ClassName applied directly to <img> element */
  imageClassName?: string;
  /** Custom title for lightbox preview */
  previewTitle?: string;
  /** Border radius style */
  rounded?: "none" | "sm" | "md" | "lg" | "full";
  onClick?: () => void;
}

const aspectClasses: Record<ImageAspectRatio, string> = {
  square: "aspect-square",
  video: "aspect-video",
  wide: "aspect-[21/9]",
  auto: "aspect-auto",
};

const fitClasses: Record<ImageFit, string> = {
  cover: "object-cover",
  contain: "object-contain",
  fill: "object-fill",
  none: "object-none",
};

const roundedClasses = {
  none: "rounded-none",
  sm: "rounded-sm",
  md: "rounded-control",
  lg: "rounded-lg",
  full: "rounded-full",
};

/**
 * Image Component - Safe image renderer with loading skeleton, error fallback, and optional lightbox preview.
 */
export function Image({
  src,
  alt = "Hình ảnh",
  aspectRatio = "auto",
  fit = "cover",
  previewable = false,
  fallback,
  className,
  imageClassName,
  previewTitle,
  rounded = "md",
  onClick,
}: ImageProps) {
  const [loading, setLoading] = useState(Boolean(src));
  const [hasError, setHasError] = useState(!src);
  const [viewerOpen, setViewerOpen] = useState(false);

  const handleImageLoad = () => {
    setLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    setLoading(false);
    setHasError(true);
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
    if (previewable && src && !hasError) {
      setViewerOpen(true);
    }
  };

  return (
    <>
      <div
        className={cn(
          "group relative inline-flex items-center justify-center overflow-hidden border border-border/60 bg-bg transition-all",
          aspectClasses[aspectRatio],
          roundedClasses[rounded],
          previewable && !hasError && "cursor-pointer hover:border-primary/60",
          className
        )}
        onClick={handleClick}
      >
        {/* Loading Skeleton */}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-bg/80 animate-pulse text-text-muted">
            <Loader2 className="size-5 animate-spin text-primary" />
          </div>
        )}

        {/* Error Fallback */}
        {hasError ? (
          fallback || (
            <div className="flex flex-col items-center justify-center p-3 text-center text-text-muted select-none">
              <ImageOff className="size-6 mb-1 opacity-60" />
              <span className="text-[11px] font-medium leading-tight">
                Không thể tải ảnh
              </span>
            </div>
          )
        ) : (
          <img
            src={src!}
            alt={alt}
            onLoad={handleImageLoad}
            onError={handleImageError}
            className={cn(
              "size-full transition-transform duration-200",
              fitClasses[fit],
              roundedClasses[rounded],
              previewable && "group-hover:scale-105",
              imageClassName
            )}
          />
        )}

        {/* Hover Overlay for Previewable Images */}
        {previewable && !hasError && !loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/35 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
            <div className="flex items-center gap-1.5 rounded-full bg-surface/90 px-2.5 py-1 text-caption font-semibold text-text shadow-sm backdrop-blur-xs">
              <Eye className="size-3.5 text-primary" />
              <span>Xem lớn</span>
            </div>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {previewable && (
        <ImageViewerModal
          open={viewerOpen}
          onOpenChange={setViewerOpen}
          src={src}
          alt={alt}
          title={previewTitle || alt}
        />
      )}
    </>
  );
}
